import { motion } from "framer-motion";
import { useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import {
  MdBusiness,
  MdOutlineViewTimeline,
  MdPerson,
  MdTimer,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import bannerLogo from "../../../Assets/Banner-Image-1.png";

const HeroSection = ({ handleOpenModal }) => {
  const navigate = useNavigate();
  const [userType, setUserType] = useState("business"); // "individual" or "business"
  const waitlistUrl = "https://portal.selfsync.ai/sign-up";

  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.8,
        ease: "easeOut",
      },
    }),
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { delay: 0.3, duration: 0.8, ease: "easeOut" },
    },
  };

  const getUserContent = () => {
    if (userType === "individual") {
      return {
        badge: "Discover Your Strengths - 30 Minutes Only",
        headline: "Take Smarter Career Decisions",
        subtitle: " – With First-Ever AI Powered Talent Assessment Platform",
        description:
          "Stop guessing. Our AI reveals your ideal career fit, team dynamic, and leadership style in minutes, so you can grow faster, perform better, and love your work",
        primaryCTA: "Take Free Personality Test",
        secondaryCTA: "See Example Results",
        benefits: [
          "Discover ideal careers for your personality",
          "Understand your work style & strengths",
          "Get insights employers value",
        ],
        stats: [
          { number: "99%", label: "Accuracy Rate" },
          { number: "5K+", label: "People Assessed" },
        ],
      };
    } else {
      return {
        badge: "Transform Your Hiring - 15 Minutes Setup",
        headline: "10X Your Team Productivity by",
        subtitle: "Hiring the Right People in Every Role",
        description:
          "Our AI-powered personality assessment helps you identify candidates who truly fit — improving culture alignment, retention, and performance from day one",
        primaryCTA: "Start Hiring Smarter",
        secondaryCTA: "See Sample Report",
        benefits: [
          "260+ Companies Trust Us",
          "3x Better Employee Retention",
          "Reduce Bad Hire Costs",
        ],
        stats: [
          { number: "99%", label: "Accuracy Rate" },
          { number: "260+", label: "Companies" },
        ],
      };
    }
  };

  const content = getUserContent();

  return (
    <motion.section
      initial="hidden"
      animate="visible"
      className="pt-32 max-md:pt-24  px-6 md:px-14 lg:px-20 overflow-hidden relative bg-gradient-to-b from-white to-gray-50"
      id="hero"
    >
      <div className="max-w-7xl mx-auto">
        {/* User Type Selector */}
        <motion.div
          className="flex justify-center mb-8"
          variants={textVariants}
          custom={0}
        >
          <div className="relative w-full md:w-auto flex md:inline-flex bg-gray-100 rounded-2xl p-1 md:px-4 gap-4 sm:gap-6">
            {/* Sliding background indicator */}
            <motion.div
              layout
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className={`absolute top-1 bottom-1 left-1 rounded-xl custom-button  ${ userType === "individual" ? "-mx-1 ":"mx-2"  }   w-1/2 sm:w-auto z-0`}
              style={{
                width: "50%",
                left: userType === "business" ? "0%" : "50%",
              }}
            />

            {/* Buttons */}
             <button
              onClick={() => setUserType("business")}
              className={`relative z-10 flex items-center justify-center gap-1 px-4 py-2 text-xs md:text-base rounded-lg font-medium transition-all w-full sm:w-auto ${
                userType === "business"
                  ? "text-white"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <MdBusiness className="text-sm md:text-lg" />
              I'm Hiring People
            </button>
            <button
              onClick={() => setUserType("individual")}
              className={`relative z-10 flex items-center justify-center pr-1 text-xs md:text-base gap-1 px-0 py-2 rounded-lg font-medium transition-all w-full sm:w-auto ${
                userType === "individual"
                  ? "text-white"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <MdPerson className="md:text-lg" />
              I'm Looking for a Job
            </button>
           
          </div>
        </motion.div>

        <div className="flex flex-col lg:flex-row items-center justify-between gap-1">
          {/* Left Text Section */}
          <div className="lg:w-1/2 text-center lg:text-left">
            {/* Hook/Badge */}
            {/* <motion.div
              className="inline-flex items-center gap-2  text-purple-700 px-2 md:px-4 py-2 rounded-full text-sm font-semibold mb-6"
              variants={textVariants}
              custom={1}
              key={content.badge}
            >
              <MdTimer className="text-lg" />
              {content.badge}
            </motion.div> */}

            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-gray-900"
              variants={textVariants}
              custom={2}
              key={content.headline}
            >
              {content.headline}{" "}
              <span className="bg-gradient-to-r from-purple-600 to-cyan-500 text-transparent bg-clip-text">
                {content.subtitle}
              </span>
            </motion.h1>

            {/* Mobile Image */}
            <motion.div
              className="lg:hidden relative mb-8"
              variants={imageVariants}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={bannerLogo}
                  alt="AI-powered personality assessment platform"
                  className="object-cover w-full h-full rounded-2xl"
                />
              </div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-purple-300 to-cyan-300 opacity-30 rounded-full blur-3xl -z-10"></div>
            </motion.div>

            <motion.p
              className="text-xl md:text-xl text-gray-700 mb-8 leading-relaxed"
              variants={textVariants}
              custom={3}
              key={content.description}
            >
              {content.description}
            </motion.p>

            {/* Benefits */}
            <motion.div
              className="flex flex-col sm:flex-row items-start justify-center lg:justify-start gap-4 sm:gap-6 mb-8 text-sm text-gray-600"
              variants={textVariants}
              custom={4}
              key={content.benefits.join("")}
            >
              {content.benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2">
                  <FaCheckCircle className="text-green-500 flex-shrink-0" />
                  <span>{benefit}</span>
                </div>
              ))}
            </motion.div>

            {/* Primary CTA */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center text-center lg:justify-start mb-6"
              variants={textVariants}
              custom={5}
            >
              <button
                type="button"
                className="custom-button text-white font-bold px-6 py-2 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg text-base transform hover:scale-105"
                onClick={() => window.open(waitlistUrl, "_blank")}
              >
                {content.primaryCTA}
              </button>

              <button
                type="button"
                className="flex justify-center items-center gap-3 font-semibold border-2 border-gray-300 text-gray-700 hover:border-purple-600 hover:text-purple-600 py-2 px-6 rounded-lg transition-all text-base text-center w-full sm:w-auto"
                onClick={() => navigate("/sample-report")}
              >
                <span className="text-center w-full flex justify-center items-center">
                  <MdOutlineViewTimeline className="text-xl text-center mr-2" />
                  {content.secondaryCTA}
                </span>
              </button>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              className="text-sm text-gray-500 space-y-2"
              variants={textVariants}
              custom={6}
            >
              <div className="flex items-center justify-center lg:justify-start gap-4 mb-2">
                <span>✓ No credit card required</span>
                <span>✓ Instant results</span>
                <span>✓ 100% private & secure</span>
              </div>
              <p className="text-xs text-gray-400 mt-4 ">
                {userType === "individual"
                  ? "Join 5,000+ professionals who discovered their ideal career path"
                  : ""}
              </p>
            </motion.div>
          </div>

          {/* Right Image for Desktop */}
          <motion.div
            className="lg:w-1/2 relative hidden lg:flex"
            variants={imageVariants}
          >
            <div className="relative overflow-hidden">
              <img
                src={bannerLogo}
                alt="Professional team collaboration and personality assessment"
                className="object-cover w-full h-full hidden md:block"
              />

              {/* Dynamic Floating Stats Cards */}
              <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {content.stats[0].number}
                  </div>
                  <div className="text-xs text-gray-600">
                    {content.stats[0].label}
                  </div>
                </div>
              </div>

              <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-500">
                    {content.stats[1].number}
                  </div>
                  <div className="text-xs text-gray-600">
                    {content.stats[1].label}
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-purple-300 to-cyan-300 opacity-30 rounded-full blur-3xl -z-10"></div>
          </motion.div>
        </div>

        {/* Bottom Trust Bar - Context Sensitive */}
        <motion.div
          className="mt-2 pt-8 border-t max-md:border-none border-gray-200 max-md:hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
        >
          {userType === "business" ? (
            <></>
          ) : (
            <>
              <p className="text-center text-sm max-md:font-bold text-gray-500 mb-4">
                Discover careers where you naturally excel
              </p>
              <div className="flex justify-center items-center gap-8 text-gray-400 text-xs">
                <span>💼 Find Your Ideal Role</span>
                <span>🎯 Understand Your Strengths</span>
                <span>📈 Boost Your Career</span>
                <span>🤝 Match Company Culture</span>
              </div>
            </>
          )}
        </motion.div>
      </div>

      {/* Background Elements */}
      <div className="absolute top-1/4 left-8 w-20 h-20 bg-purple-200 rounded-full opacity-50 blur-xl"></div>
      <div className="absolute bottom-1/4 right-8 w-16 h-16 bg-cyan-200 rounded-full opacity-50 blur-xl"></div>
    </motion.section>
  );
};

export default HeroSection;
